import styled, { css } from 'styled-components';
import { cardSurface, headingFont, overlineLabel, pillShape, truncateLine } from './mixins';

// =============================================================================
// Empty State — used for "no results", "no documents", error fallback, etc.
// =============================================================================

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing[16]} ${theme.spacing[6]}`};
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  width: 5rem;
  height: 5rem;
  color: ${({ theme }) => theme.colors.gray300};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const EmptyStateTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const EmptyStateDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 25rem;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
`;

// =============================================================================
// Surface Card — bordered card with hover elevation
// =============================================================================

export const SurfaceCard = styled.div<{ $accent?: boolean }>`
  ${cardSurface}
  padding: ${({ theme }) => theme.spacing[5]};
  transition: box-shadow ${({ theme }) => theme.transitions.base};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  ${({ $accent, theme }) =>
    $accent &&
    css`
      border-top: 3px solid ${theme.colors.accent};
    `}
`;

// =============================================================================
// Table Wrapper — rounded container for tables
// =============================================================================

export const TableWrapper = styled.div`
  ${cardSurface}
  overflow: hidden;

  .table {
    margin-bottom: 0;

    thead th {
      ${overlineLabel}
      background-color: ${({ theme }) => theme.colors.primary50};
      border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
      padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
      white-space: nowrap;
    }

    tbody td {
      padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
      vertical-align: middle;
      font-size: ${({ theme }) => theme.fontSizes.sm};
      border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
    }

    tbody tr {
      transition: background-color ${({ theme }) => theme.transitions.fast};
    }

    tbody tr:last-child td {
      border-bottom: none;
    }
  }
`;

// =============================================================================
// Filter Bar — wrapper for filter dropdowns
// =============================================================================

export const FilterBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
  align-items: center;

  .form-select {
    max-width: 240px;
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }

  .form-control {
    max-width: 400px;
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

// =============================================================================
// Stat Card — KPI-style metric card
// =============================================================================

export const StatCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

export const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

export const StatValue = styled.span`
  ${headingFont}
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1;
`;

export const StatLabel = styled.span`
  ${overlineLabel}
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

// =============================================================================
// Status Badge — pill-shaped status indicator
// =============================================================================

export const StatusBadge = styled.span<{ $color?: string }>`
  ${pillShape}
  background-color: ${({ $color, theme }) => $color || theme.colors.secondary};
  color: ${({ theme }) => theme.colors.textInverse};
`;

// =============================================================================
// Typography helpers
// =============================================================================

export const TextCaption = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const TextMutedSmall = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const TextTruncate = styled.div`
  ${truncateLine}
`;

export const LoadingCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
`;

export const ClickableTr = styled.tr`
  cursor: pointer;
`;

export const HiddenFileInput = styled.input.attrs({ type: 'file' })`
  display: none;
`;

export const ActionsTh = styled.th`
  width: 100px;
`;

// =============================================================================
// Responsive helpers — toggle between desktop table and mobile cards
// =============================================================================

export const DesktopOnly = styled.div`
  @media (max-width: 767.98px) {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  display: none;

  @media (max-width: 767.98px) {
    display: block;
  }
`;

// =============================================================================
// Mobile Card — compact card for list items on small screens
// =============================================================================

export const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const MobileCard = styled.div<{ $clickable?: boolean }>`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.xs};
  transition: box-shadow ${({ theme }) => theme.transitions.fast};
  border-left: 3px solid transparent;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.sm};
    border-left-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const MobileCardTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const MobileCardMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MobileCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const MobileCardBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const MobileCardField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const MobileCardFieldLabel = styled.span`
  ${overlineLabel}
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.7;
`;

export const MobileCardActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray100};
`;
